/**
 * IndexedDB cache operations for VybeTraderDB (ohlcData + ohlcByDay).
 * Shared by storage inventory and clear helpers.
 */

import { openDB, type IDBPDatabase } from "idb";
import {
  OHLC_BY_DAY_STORE,
  generateAccountSymbolKey,
} from "@/vybe-trader-client/chart/indexdb";

const DB_NAME = "VybeTraderDB";
const DB_VERSION = 4;
const OHLC_DATA_STORE = "ohlcData";

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(OHLC_DATA_STORE)) {
          db.createObjectStore(OHLC_DATA_STORE, { keyPath: "key" });
        }
        if (oldVersion < 4 && !db.objectStoreNames.contains(OHLC_BY_DAY_STORE)) {
          db.createObjectStore(OHLC_BY_DAY_STORE, { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

export function accountPrefix(companyName: string, serverName: string): string {
  return `${companyName}_${serverName}_`;
}

/** ~56 bytes per serialized 1m bar; avoids JSON.stringify on full OHLC payloads. */
const BYTES_PER_BAR = 56;
const RECORD_OVERHEAD = 96;

function estimateRecordBytes(value: unknown): number {
  if (value == null || typeof value !== "object") return RECORD_OVERHEAD;
  const rec = value as Record<string, unknown>;
  const bars = rec.bars ?? rec.data;
  let count = 0;
  if (Array.isArray(bars)) count = bars.length;
  else if (typeof rec.count === "number") count = rec.count;
  return RECORD_OVERHEAD + count * BYTES_PER_BAR;
}

/** Parse IDB key into accountSymbolKey (company_server_symbol) when possible. */
export function parseMarketDataKey(key: string): string | null {
  const pipe = key.indexOf("|");
  if (pipe >= 0) return key.slice(0, pipe);
  return key;
}

export function symbolFromAccountSymbolKey(
  accountSymbolKey: string,
  prefix: string,
): string {
  if (accountSymbolKey.startsWith(prefix)) {
    return accountSymbolKey.slice(prefix.length);
  }
  const last = accountSymbolKey.lastIndexOf("_");
  return last >= 0 ? accountSymbolKey.slice(last + 1) : accountSymbolKey;
}

export interface SymbolByteMap {
  [symbol: string]: number;
}

export interface AccountIdbScan {
  prefix: string;
  symbols: SymbolByteMap;
  totalBytes: number;
}

export interface IdbMarketDataScan {
  byPrefix: Map<string, AccountIdbScan>;
  orphan: AccountIdbScan;
}

export async function scanIdbMarketData(
  knownPrefixes: string[],
): Promise<IdbMarketDataScan> {
  const known = new Set(knownPrefixes);
  const byPrefix = new Map<string, AccountIdbScan>();
  const orphan: AccountIdbScan = {
    prefix: "__orphan__",
    symbols: {},
    totalBytes: 0,
  };

  const ensure = (prefix: string): AccountIdbScan => {
    let row = byPrefix.get(prefix);
    if (!row) {
      row = { prefix, symbols: {}, totalBytes: 0 };
      byPrefix.set(prefix, row);
    }
    return row;
  };

  const addBytes = (accountSymbolKey: string, bytes: number) => {
    const matched = knownPrefixes.find((p) => accountSymbolKey.startsWith(p));
    if (matched) {
      const row = ensure(matched);
      const symbol = symbolFromAccountSymbolKey(accountSymbolKey, matched);
      row.symbols[symbol] = (row.symbols[symbol] ?? 0) + bytes;
      row.totalBytes += bytes;
    } else {
      orphan.symbols[accountSymbolKey] =
        (orphan.symbols[accountSymbolKey] ?? 0) + bytes;
      orphan.totalBytes += bytes;
    }
  };

  const db = await getDb();
  const storeNames = [OHLC_DATA_STORE, OHLC_BY_DAY_STORE] as const;

  for (const storeName of storeNames) {
    if (!db.objectStoreNames.contains(storeName)) continue;
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    let cursor = await store.openCursor();
    while (cursor) {
      const accountSymbolKey = parseMarketDataKey(String(cursor.key));
      if (accountSymbolKey) {
        addBytes(accountSymbolKey, estimateRecordBytes(cursor.value));
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  }

  return { byPrefix, orphan };
}

async function deleteKeysMatching(
  storeName: string,
  match: (key: string) => boolean,
): Promise<number> {
  const db = await getDb();
  if (!db.objectStoreNames.contains(storeName)) return 0;
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  const keys: string[] = [];
  let cursor = await store.openCursor();
  while (cursor) {
    if (match(String(cursor.key))) keys.push(String(cursor.key));
    cursor = await cursor.continue();
  }
  for (const key of keys) await store.delete(key);
  await tx.done;
  return keys.length;
}

export async function clearIdbAccountMarketData(
  companyName: string,
  serverName: string,
): Promise<void> {
  const prefix = accountPrefix(companyName, serverName);
  await deleteKeysMatching(OHLC_DATA_STORE, (k) => k.startsWith(prefix));
  await deleteKeysMatching(OHLC_BY_DAY_STORE, (k) => {
    const symKey = parseMarketDataKey(k);
    return symKey != null && symKey.startsWith(prefix);
  });
}

export async function clearIdbSymbolMarketData(
  companyName: string,
  serverName: string,
  symbol: string,
): Promise<void> {
  const symbolKey = generateAccountSymbolKey(
    companyName,
    serverName,
    symbol,
  );
  await deleteKeysMatching(OHLC_DATA_STORE, (k) => k === symbolKey);
  const lower = `${symbolKey}|`;
  const upper = `${symbolKey}|\uffff`;
  await deleteKeysMatching(OHLC_BY_DAY_STORE, (k) => k >= lower && k <= upper);
}

export async function clearAllIdbMarketData(): Promise<void> {
  const db = await getDb();
  const stores = [OHLC_DATA_STORE, OHLC_BY_DAY_STORE];
  const tx = db.transaction(
    stores.filter((s) => db.objectStoreNames.contains(s)),
    "readwrite",
  );
  for (const name of stores) {
    if (db.objectStoreNames.contains(name)) {
      await tx.objectStore(name).clear();
    }
  }
  await tx.done;
}

export async function clearIdbOrphanData(knownPrefixes: string[]): Promise<void> {
  const matchKnown = (accountSymbolKey: string) =>
    knownPrefixes.some((p) => accountSymbolKey.startsWith(p));

  await deleteKeysMatching(OHLC_DATA_STORE, (k) => {
    const symKey = parseMarketDataKey(k);
    return symKey != null && !matchKnown(symKey);
  });
  await deleteKeysMatching(OHLC_BY_DAY_STORE, (k) => {
    const symKey = parseMarketDataKey(k);
    return symKey != null && !matchKnown(symKey);
  });
}
